import { Stack, Text, styled } from '@ltvco/refresh-lib/theme';
import React, { KeyboardEvent } from 'react';

interface ReportSectionProps {
  title: string;
  subtitle?: string;
  count: number;
  onClick: () => void;
  actions?: React.ReactNode;
}

const SectionRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2.5, 0),
  cursor: 'pointer',
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
    borderRadius: 4,
  },
}));

export const ActionIconButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  borderRadius: '50%',
  padding: theme.spacing(0.5),
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const ReportSection = ({
  title,
  subtitle,
  count,
  onClick,
  actions,
}: ReportSectionProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <SectionRow
      role="button"
      tabIndex={0}
      aria-label={`${title}, ${count} reports`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <Stack spacing={0.25}>
        <Text variant="h4" fontWeight="bold">
          {title}
        </Text>
        {subtitle && (
          <Text variant="caption" color="text.secondary">
            {subtitle}
          </Text>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Text
          variant="h4"
          fontWeight="bold"
          color="text.primary"
          sx={
            actions
              ? (theme) => ({
                  pr: theme.spacing(1),
                  borderRight: `1px solid ${theme.palette.divider}`,
                })
              : undefined
          }
        >
          {count}
        </Text>
        {actions}
      </Stack>
    </SectionRow>
  );
};
